import { useMemo } from 'react';
import { addDays, isTimesheetLocked, type IsoDate } from '@tt/shared';
import { useMe } from './queries';

/**
 * The last day a closed period covers, which is the day *before* the lock date.
 *
 * The setting is "hours dated before this day are closed", because that is the boundary an
 * administrator types: closing September is setting 1 October. Every sentence shown to a person
 * names the last closed day instead, because "nothing up to 30 September can be changed" is what
 * being locked out of September actually feels like.
 */
export function closedThrough(lockDate: IsoDate): IsoDate {
  return addDays(lockDate, -1);
}

export interface TimesheetLock {
  /** Hours dated before this day are closed; `null` when nothing is closed. */
  lockDate: IsoDate | null;
  /** True when the signed-in user may not write hours dated `date`. */
  isLocked: (date: string) => boolean;
  /** True when a closed period binds the signed-in user at all — never for an administrator. */
  applies: boolean;
}

/**
 * The closed period as it applies to the signed-in user (design A18).
 *
 * The answer comes from the same `isTimesheetLocked` the API refuses writes with, so a cell the
 * grid lets someone type in is a cell the server will accept. It is always about the signed-in
 * user: an administrator correcting somebody else's week is exempt, which is what their own role
 * already says — there is no need to know whose timesheet is on screen.
 *
 * While `/me` is still loading nothing is locked. Assuming the other way would grey out every
 * timesheet for a moment on each load, and the server refuses anything that slips through.
 */
export function useTimesheetLock(): TimesheetLock {
  const { data: me } = useMe();
  return useMemo(() => {
    const lockDate = me?.organization.timesheetLockDate ?? null;
    const role = me?.role ?? 'USER';
    return {
      lockDate,
      isLocked: (date: string) => isTimesheetLocked(date, lockDate, role),
      applies: lockDate !== null && role !== 'ADMIN',
    };
  }, [me]);
}
